// Admin-Panel: gemeinsame Bausteine (Fehleranzeige mit 403-Meldung, Kein-Zugriff, Nutzer-Auswahl, Abschnitt).
import type { AdminUserDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { adminApi } from '../api';
import { displayName } from '../format';
import { Badge, ErrorBox } from '../ui';
import { useDebounced, useReportForbidden } from './hooks';
import { ADMIN_USERS_KEY } from './realtime';
import './admin.css';

/** Fehlerbox; 403 schaltet zusätzlich das ganze Panel auf „Kein Zugriff". */
export function AdminError(props: { error: unknown; testId?: string }) {
  useReportForbidden(props.error);
  return <ErrorBox error={props.error} testId={props.testId ?? 'admin-error'} />;
}

export function AdminForbidden() {
  return (
    <main className="lfx-page" data-testid="admin-guard-forbidden">
      <div className="lfx-card lfx-admin-guard">
        <h1 className="lfx-page__title">Kein Zugriff</h1>
        <p className="lfx-muted">Das Admin-Panel ist nur für Plattform-Admins zugänglich.</p>
        <Link to="/" className="lfx-link" data-testid="admin-guard-home">
          Zurück zur Startseite
        </Link>
      </div>
    </main>
  );
}

export function AdminLoginRequired() {
  return (
    <main className="lfx-page" data-testid="admin-guard-login">
      <div className="lfx-card lfx-admin-guard">
        <h1 className="lfx-page__title">Bitte anmelden</h1>
        <p className="lfx-muted">Melde dich mit einem Admin-Konto an, um das Admin-Panel zu öffnen.</p>
        <Link to="/login" className="lfx-link" data-testid="admin-guard-login-link">
          Zur Anmeldung
        </Link>
      </div>
    </main>
  );
}

export function AdminSection(props: { title: string; testId: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="lfx-admin-section" data-testid={props.testId} aria-label={props.title}>
      <header className="lfx-row lfx-row--between lfx-admin-section__head">
        <h2 className="lfx-admin-section__title">{props.title}</h2>
        {props.actions && <div className="lfx-row">{props.actions}</div>}
      </header>
      {props.children}
    </section>
  );
}

export function UserBadges(props: { user: Pick<AdminUserDto, 'id' | 'isAdmin' | 'isBot' | 'isShadow'> & { disabled?: boolean } }) {
  const u = props.user;
  return (
    <span className="lfx-row lfx-admin-badges">
      {u.isAdmin && (
        <Badge tone="accent" testId={`admin-user-badge-admin-${u.id}`}>
          Admin
        </Badge>
      )}
      {u.disabled && (
        <Badge tone="danger" testId={`admin-user-badge-disabled-${u.id}`}>
          gesperrt
        </Badge>
      )}
      {u.isBot && (
        <Badge tone="neutral" testId={`admin-user-badge-bot-${u.id}`}>
          Bot
        </Badge>
      )}
      {u.isShadow && (
        <Badge tone="mirror" testId={`admin-user-badge-shadow-${u.id}`}>
          Schatten
        </Badge>
      )}
    </span>
  );
}

/** Nutzer per Admin-Suche auswählen (Besitzer übertragen, Audit-Filter). */
export function UserPicker(props: { testId: string; label: string; onSelect: (u: AdminUserDto) => void; excludeId?: string }) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim(), 250);
  const id = useId();
  const listId = useId();
  const query = useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'picker', debounced],
    queryFn: () => adminApi.users({ q: debounced, limit: 8 }),
    enabled: debounced.length > 0,
  });
  const items = (query.data?.items ?? []).filter((u) => u.id !== props.excludeId);
  return (
    <div className="lfx-field lfx-admin-picker" data-testid={props.testId}>
      <label className="lfx-field__label" htmlFor={id}>
        {props.label}
      </label>
      <input
        id={id}
        className="lfx-input"
        type="search"
        value={q}
        placeholder="Name oder Benutzername"
        autoComplete="off"
        aria-controls={listId}
        data-testid={`${props.testId}-input`}
        onChange={(e) => setQ(e.target.value)}
      />
      {query.error ? <AdminError error={query.error} testId={`${props.testId}-error`} /> : null}
      {debounced && (
        <ul className="lfx-admin-picker__list" id={listId} data-testid={`${props.testId}-results`}>
          {query.isLoading && <li className="lfx-muted">Sucht …</li>}
          {!query.isLoading && items.length === 0 && <li className="lfx-muted">Keine Treffer</li>}
          {items.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className="lfx-admin-picker__item"
                data-testid={`${props.testId}-option-${u.id}`}
                onClick={() => {
                  props.onSelect(u);
                  setQ('');
                }}
              >
                <span>{displayName(u)}</span> <span className="lfx-muted">@{u.username}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
